import React from "react";
import { Link } from "@/lib/router-compat";
import { BlogPost as BlogPostType } from "@/data/blogPosts";
import { ArrowRight, Clock } from "lucide-react";

interface ArticleRelatedSectionProps {
  relatedPosts: BlogPostType[];
  postCategory: string;
  postCategorySlug: string;
  relatedCategories: string[];
  dedupedPosts: BlogPostType[];
  categoryToSlug: (cat: string) => string;
}

export function ArticleRelatedSection({
  relatedPosts,
  postCategory,
  postCategorySlug,
  relatedCategories,
  dedupedPosts,
  categoryToSlug,
}: ArticleRelatedSectionProps) {
  return (
    <>
      {/* Browse by Category */}
      <div className="mt-16 pt-8 border-t border-[#082E67]/10">
        <h3 className="text-lg font-display font-bold text-[#071E61] mb-6">
          Browse by Category
        </h3>
        <div className="flex flex-wrap gap-3">
          <Link
            to={`/blog/category/${postCategorySlug}`}
            className="group flex items-center gap-3 bg-[#EDF7FF] border border-[#0B7CF2]/30 rounded-xl px-5 py-3 hover:bg-[#E0F2FE] transition-colors"
          >
            <span className="text-sm font-semibold text-[#0B7CF2]">
              {postCategory}
            </span>
            <span className="text-xs font-mono text-[#0B7CF2]/70 font-semibold">
              {dedupedPosts.filter((p) => p.category === postCategory).length}{" "}
              articles
            </span>
          </Link>
          {relatedCategories.map((cat) => {
            const catSlug = categoryToSlug(cat);
            const count = dedupedPosts.filter((p) => p.category === cat).length;
            return (
              <Link
                key={cat}
                to={`/blog/category/${catSlug}`}
                className="group flex items-center gap-3 bg-white border border-[#082E67]/10 rounded-xl px-5 py-3 hover:bg-[#F8FCFF] hover:border-[#0B7CF2]/30 transition-colors shadow-xs"
              >
                <span className="text-sm font-medium group-hover:text-[#0B7CF2] transition-colors text-[#082D67]">
                  {cat}
                </span>
                <span className="text-xs font-mono text-[#6A7D97]">
                  {count} articles
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {relatedPosts.length > 0 && (
        <div className="mt-20 pt-14 border-t border-[#082E67]/10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="text-[11px] font-bold tracking-widest uppercase text-[#0B7CF2] block mb-1">
                Curated Recommendations
              </span>
              <h3
                className="font-display font-extrabold text-[#071E61]"
                style={{
                  fontSize: "clamp(24px, 2.6vw, 32px)",
                  letterSpacing: "-0.025em",
                }}
              >
                More from the Systems Log
              </h3>
            </div>
            <Link
              to="/blog"
              className="text-xs font-bold text-[#0B7CF2] hover:underline hidden sm:flex items-center gap-1"
            >
              <span>View all articles</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.map((relatedPost) => (
              <Link
                key={relatedPost.id}
                to={`/blog/${relatedPost.slug}`}
                className="group relative flex flex-col bg-white border border-[#082E67]/10 rounded-2xl p-6 hover:border-[#0B7CF2]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(8,42,102,0.08)] overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-2 text-xs font-mono mb-4 relative z-10 text-[#6A7D97]">
                  <span className="flex items-center gap-1 bg-[#EDF7FF] text-[#0B7CF2] font-semibold px-2.5 py-0.5 rounded-full text-[11px]">
                    {relatedPost.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-[#6A7D97]" />
                    {relatedPost.readTime}
                  </span>
                </div>

                <h4 className="text-[17px] font-display font-bold text-[#071E61] mb-2 group-hover:text-[#0B7CF2] transition-colors leading-snug relative z-10 line-clamp-2">
                  {relatedPost.title}
                </h4>

                <p className="text-sm leading-relaxed mb-4 flex-grow relative z-10 line-clamp-2 text-[#536B89]">
                  {relatedPost.excerpt}
                </p>

                <div className="flex items-center text-[#0B7CF2] text-xs font-bold mt-auto group-hover:translate-x-1 transition-transform relative z-10">
                  Read Article <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
